import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Chip,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tooltip,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sms as SmsIcon,
  Science as SandboxIcon,
  Verified as ProductionIcon,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  History as HistoryIcon,
  Settings as ConfigIcon,
  Description as TemplateIcon,
  Timer as TimerIcon,
  Dashboard as DashboardIcon,
  Refresh as RefreshIcon,
  Phone as PhoneIcon,
  ErrorOutline,
  DoneAll,
  HourglassEmpty,
  TrendingUp,
  AttachMoney,
  RecordVoiceOver as VoiceIcon,
} from '@mui/icons-material';
import {
  settingsService,
  TwilioConfig,
  SmsTemplate,
  SmsLog,
  SmsStats,
  SmsInterval,
} from '../../../services';

type MainTab = 'dashboard' | 'config' | 'templates' | 'history' | 'intervals';
type Environment = 'sandbox' | 'production';

const SmsPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<MainTab>('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dashboard data
  const [stats, setStats] = useState<SmsStats | null>(null);
  const [recentLogs, setRecentLogs] = useState<SmsLog[]>([]);

  // Twilio configs
  const [configs, setConfigs] = useState<TwilioConfig[]>([]);
  const [configPage, setConfigPage] = useState(0);
  const [configEnvTab, setConfigEnvTab] = useState<Environment>('sandbox');
  const [configDialog, setConfigDialog] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<TwilioConfig | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [configForm, setConfigForm] = useState({
    description: '',
    accountSid: '',
    authToken: '',
    phoneNumber: '+14246881297',
    messagingServiceSid: '',
    voiceWebhookUrl: '',
    environment: 'sandbox' as Environment,
    active: true,
  });

  // Templates
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [templatePage, setTemplatePage] = useState(0);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SmsTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    code: '',
    name: '',
    content: '',
    type: 'transacional' as SmsTemplate['type'],
    active: true,
  });

  // SMS Logs
  const [logs, setLogs] = useState<SmsLog[]>([]);
  const [logPage, setLogPage] = useState(0);
  const [logTotal, setLogTotal] = useState(0);
  const [logFilters, setLogFilters] = useState({
    status: '',
    phoneNumber: '',
  });

  // Intervals
  const [intervals, setIntervals] = useState<SmsInterval[]>([]);
  const [intervalPage, setIntervalPage] = useState(0);
  const [intervalDialog, setIntervalDialog] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState<SmsInterval | null>(null);
  const [intervalForm, setIntervalForm] = useState({
    code: '',
    description: '',
    minutes: 60,
    active: true,
  });

  // Send SMS dialog
  const [sendDialog, setSendDialog] = useState(false);
  const [sendForm, setSendForm] = useState({
    to: '',
    templateCode: '',
    message: '',
    variables: {} as Record<string, string>,
  });
  const [sending, setSending] = useState(false);

  // Fetch all data
  const fetchDashboard = useCallback(async () => {
    try {
      const [statsRes, logsRes] = await Promise.all([
        settingsService.getSmsStats(),
        settingsService.getSmsLogs({ limit: 5 }),
      ]);
      setStats(statsRes.data);
      setRecentLogs(logsRes.data || []);
    } catch (err: any) {
      console.error('Error fetching dashboard:', err);
    }
  }, []);

  const fetchConfigs = useCallback(async () => {
    try {
      const response = await settingsService.getTwilioConfigs();
      setConfigs(response.data || []);
    } catch (err: any) {
      console.error('Error fetching configs:', err);
      setError(err.message || t('settings.sms.errorLoadingConfigs'));
    }
  }, [t]);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await settingsService.getSmsTemplates();
      setTemplates(response.data || []);
    } catch (err: any) {
      console.error('Error fetching templates:', err);
      setError(err.message || t('settings.sms.errorLoadingTemplates'));
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const response = await settingsService.getSmsLogs({
        page: logPage + 1,
        limit: 10,
        status: logFilters.status || undefined,
        phoneNumber: logFilters.phoneNumber || undefined,
      });
      setLogs(response.data || []);
      setLogTotal(response.pagination?.total || 0);
    } catch (err: any) {
      console.error('Error fetching logs:', err);
      setError(err.message || t('settings.sms.errorLoadingHistory'));
    }
  }, [logPage, logFilters]);

  const fetchIntervals = useCallback(async () => {
    try {
      const response = await settingsService.getSmsIntervals();
      setIntervals(response.data || []);
    } catch (err: any) {
      console.error('Error fetching intervals:', err);
      setError(err.message || t('settings.sms.errorLoadingIntervals'));
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchDashboard(),
          fetchConfigs(),
          fetchTemplates(),
          fetchIntervals(),
        ]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchDashboard, fetchConfigs, fetchTemplates, fetchIntervals]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchLogs();
    }
  }, [activeTab, fetchLogs]);

  // Config handlers
  const handleOpenConfigDialog = (config?: TwilioConfig) => {
    if (config) {
      setSelectedConfig(config);
      setConfigForm({
        description: config.description || '',
        accountSid: config.accountSid || '',
        authToken: config.authToken || '',
        phoneNumber: config.phoneNumber || '+14246881297',
        messagingServiceSid: config.messagingServiceSid || '',
        voiceWebhookUrl: config.voiceWebhookUrl || '',
        environment: (config.environment?.toLowerCase() === 'production' ? 'production' : 'sandbox') as Environment,
        active: config.active === 'S',
      });
    } else {
      setSelectedConfig(null);
      setConfigForm({
        description: '',
        accountSid: '',
        authToken: '',
        phoneNumber: '+14246881297',
        messagingServiceSid: '',
        voiceWebhookUrl: '',
        environment: configEnvTab,
        active: true,
      });
    }
    setShowToken(false);
    setConfigDialog(true);
  };

  const handleSaveConfig = async () => {
    try {
      setError(null);
      const data = {
        description: configForm.description,
        accountSid: configForm.accountSid,
        authToken: configForm.authToken,
        phoneNumber: configForm.phoneNumber || undefined,
        messagingServiceSid: configForm.messagingServiceSid || undefined,
        voiceWebhookUrl: configForm.voiceWebhookUrl || undefined,
        environment: configForm.environment,
        active: configForm.active,
      };
      if (selectedConfig) {
        await settingsService.updateTwilioConfig(selectedConfig.id, data);
        setSuccess(t('settings.sms.configUpdated'));
      } else {
        await settingsService.createTwilioConfig(data);
        setSuccess(t('settings.sms.configCreated'));
      }
      setConfigDialog(false);
      fetchConfigs();
    } catch (err: any) {
      setError(err.message || t('settings.sms.errorSavingConfig'));
    }
  };

  const handleDeleteConfig = async (config: TwilioConfig) => {
    if (!window.confirm(t('settings.sms.confirmDeleteConfig', { name: config.description }))) return;
    try {
      await settingsService.deleteTwilioConfig(config.id);
      setSuccess(t('settings.sms.configDeleted'));
      fetchConfigs();
    } catch (err: any) {
      setError(err.message || t('settings.sms.errorDeleting'));
    }
  };

  // Template handlers
  const handleOpenTemplateDialog = (template?: SmsTemplate) => {
    if (template) {
      setSelectedTemplate(template);
      setTemplateForm({
        code: template.code,
        name: template.name,
        content: template.content,
        type: template.type,
        active: template.active === 'S',
      });
    } else {
      setSelectedTemplate(null);
      setTemplateForm({
        code: '',
        name: '',
        content: '',
        type: 'transacional',
        active: true,
      });
    }
    setTemplateDialog(true);
  };

  const handleSaveTemplate = async () => {
    try {
      setError(null);
      const data = {
        code: templateForm.code,
        name: templateForm.name,
        content: templateForm.content,
        type: templateForm.type,
        active: templateForm.active,
      };
      if (selectedTemplate) {
        await settingsService.updateSmsTemplate(selectedTemplate.id, data);
        setSuccess(t('settings.sms.templateUpdated'));
      } else {
        await settingsService.createSmsTemplate(data);
        setSuccess(t('settings.sms.templateCreated'));
      }
      setTemplateDialog(false);
      fetchTemplates();
    } catch (err: any) {
      setError(err.message || t('settings.sms.errorSavingTemplate'));
    }
  };

  const handleDeleteTemplate = async (template: SmsTemplate) => {
    if (!window.confirm(t('settings.sms.confirmDeleteTemplate', { name: template.name }))) return;
    try {
      await settingsService.deleteSmsTemplate(template.id);
      setSuccess(t('settings.sms.templateDeleted'));
      fetchTemplates();
    } catch (err: any) {
      setError(err.message || t('settings.sms.errorDeleting'));
    }
  };

  // Interval handlers
  const handleOpenIntervalDialog = (interval?: SmsInterval) => {
    if (interval) {
      setSelectedInterval(interval);
      setIntervalForm({
        code: interval.code,
        description: interval.description,
        minutes: interval.minutes,
        active: interval.active === 'S',
      });
    } else {
      setSelectedInterval(null);
      setIntervalForm({
        code: '',
        description: '',
        minutes: 60,
        active: true,
      });
    }
    setIntervalDialog(true);
  };

  const handleSaveInterval = async () => {
    try {
      setError(null);
      if (selectedInterval) {
        await settingsService.updateSmsInterval(selectedInterval.id, {
          description: intervalForm.description,
          minutes: intervalForm.minutes,
          active: intervalForm.active ? 'S' : 'N',
        });
        setSuccess(t('settings.sms.intervalUpdated'));
      } else {
        await settingsService.createSmsInterval({
          code: intervalForm.code,
          description: intervalForm.description,
          minutes: intervalForm.minutes,
        });
        setSuccess(t('settings.sms.intervalCreated'));
      }
      setIntervalDialog(false);
      fetchIntervals();
    } catch (err: any) {
      setError(err.message || t('settings.sms.errorSavingInterval'));
    }
  };

  const handleDeleteInterval = async (interval: SmsInterval) => {
    if (!window.confirm(t('settings.sms.confirmDeleteInterval', { name: interval.description }))) return;
    try {
      await settingsService.deleteSmsInterval(interval.id);
      setSuccess(t('settings.sms.intervalDeleted'));
      fetchIntervals();
    } catch (err: any) {
      setError(err.message || t('settings.sms.errorDeleting'));
    }
  };

  // Send SMS handler
  const handleSendSms = async () => {
    setSending(true);
    try {
      setError(null);
      await settingsService.sendSms({
        to: sendForm.to,
        templateCode: sendForm.templateCode || undefined,
        message: sendForm.message || undefined,
        variables: Object.keys(sendForm.variables).length > 0 ? sendForm.variables : undefined,
      });
      setSuccess(t('settings.sms.smsSent'));
      setSendDialog(false);
      setSendForm({ to: '', templateCode: '', message: '', variables: {} });
      fetchDashboard();
      fetchLogs();
    } catch (err: any) {
      setError(err.message || t('settings.sms.errorSendingSms'));
    } finally {
      setSending(false);
    }
  };

  // Status helpers
  const getStatusChip = (status: string) => {
    const statusMap: Record<string, { color: 'success' | 'warning' | 'error' | 'default' | 'info'; icon: React.ReactNode }> = {
      enviado: { color: 'success', icon: <CheckCircle fontSize="small" /> },
      entregue: { color: 'success', icon: <DoneAll fontSize="small" /> },
      pendente: { color: 'warning', icon: <HourglassEmpty fontSize="small" /> },
      falhou: { color: 'error', icon: <ErrorOutline fontSize="small" /> },
      rejeitado: { color: 'error', icon: <Cancel fontSize="small" /> },
    };
    const config = statusMap[status] || { color: 'default' as const, icon: null };
    return <Chip size="small" label={status} color={config.color} icon={config.icon as any} />;
  };

  const getTemplateTypeChip = (type: string) => {
    const typeMap: Record<string, { color: 'primary' | 'secondary' | 'success' | 'warning'; label: string }> = {
      transacional: { color: 'primary', label: t('settings.sms.transactional') },
      marketing: { color: 'secondary', label: t('settings.sms.marketing') },
      notificacao: { color: 'success', label: t('settings.sms.notification') },
      otp: { color: 'warning', label: t('settings.sms.otp') },
    };
    const config = typeMap[type] || { color: 'primary' as const, label: type };
    return <Chip size="small" label={config.label} color={config.color} variant="outlined" />;
  };

  // Filter configs by environment
  const filteredConfigs = configs.filter(c => {
    const env = c.environment?.toLowerCase() || 'sandbox';
    return env === configEnvTab;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SmsIcon sx={{ fontSize: 32 }} color="primary" />
          <Typography variant="h5" fontWeight={600}>
            {t('settings.sms.title')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={() => setSendDialog(true)}
          disabled={configs.filter(c => c.active === 'S').length === 0}
        >
          {t('settings.sms.sendSms')}
        </Button>
      </Box>

      {/* Main Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="dashboard" icon={<DashboardIcon />} iconPosition="start" label={t('settings.sms.dashboard')} />
          <Tab value="config" icon={<ConfigIcon />} iconPosition="start" label={t('settings.sms.configs')} />
          <Tab value="templates" icon={<TemplateIcon />} iconPosition="start" label={t('settings.sms.templates')} />
          <Tab value="history" icon={<HistoryIcon />} iconPosition="start" label={t('settings.sms.history')} />
          <Tab value="intervals" icon={<TimerIcon />} iconPosition="start" label={t('settings.sms.intervals')} />
        </Tabs>
      </Paper>

      {/* ==================== DASHBOARD TAB ==================== */}
      {activeTab === 'dashboard' && (
        <Box>
          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SmsIcon color="primary" fontSize="large" />
                    <Box>
                      <Typography variant="h4" fontWeight={700}>{stats?.total || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('settings.sms.totalSms')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DoneAll color="success" fontSize="large" />
                    <Box>
                      <Typography variant="h4" fontWeight={700}>{stats?.entregue || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('settings.sms.delivered')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorOutline color="error" fontSize="large" />
                    <Box>
                      <Typography variant="h4" fontWeight={700}>{stats?.falhou || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('settings.sms.failed')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachMoney color="warning" fontSize="large" />
                    <Box>
                      <Typography variant="h4" fontWeight={700}>
                        {stats?.custoTotal?.toFixed(2) || '0.00'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{t('settings.sms.totalCost')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Today & Last 7 Days */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {t('settings.sms.today')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 4 }}>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="primary">
                        {stats?.hoje?.total || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('settings.sms.sent')}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="success.main">
                        {stats?.hoje?.enviado || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('settings.sms.successLabel')}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="error.main">
                        {stats?.hoje?.falhou || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('settings.sms.failed')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {t('settings.sms.last7Days')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 4 }}>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="primary">
                        {stats?.ultimosSete?.total || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('settings.sms.sent')}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="success.main">
                        {stats?.ultimosSete?.enviado || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('settings.sms.successLabel')}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="error.main">
                        {stats?.ultimosSete?.falhou || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('settings.sms.failed')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quick Status */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {t('settings.sms.activeConfigs')}
                  </Typography>
                  <Typography variant="h3" fontWeight={700} color="success.main">
                    {configs.filter(c => c.active === 'S').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.sms.ofConfigs', { count: configs.length })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {t('settings.sms.activeTemplates')}
                  </Typography>
                  <Typography variant="h3" fontWeight={700} color="primary">
                    {templates.filter(t => t.active === 'S').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.sms.ofTemplates', { count: templates.length })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {t('settings.sms.segmentsSent')}
                  </Typography>
                  <Typography variant="h3" fontWeight={700} color="info.main">
                    {stats?.segmentosTotal || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.sms.smsSegments')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Recent Activity */}
          <Paper sx={{ mt: 3 }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t('settings.sms.recentActivity')}
              </Typography>
              <IconButton size="small" onClick={fetchDashboard}>
                <RefreshIcon />
              </IconButton>
            </Box>
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('settings.sms.phone')}</TableCell>
                    <TableCell>{t('settings.sms.template')}</TableCell>
                    <TableCell>{t('common.status')}</TableCell>
                    <TableCell>{t('common.date')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">{t('settings.sms.noSmsSent')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentLogs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {log.phoneNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>{log.templateName || '-'}</TableCell>
                        <TableCell>{getStatusChip(log.status)}</TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {log.createdAt ? new Date(log.createdAt).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* ==================== CONFIG TAB ==================== */}
      {activeTab === 'config' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('settings.sms.twilioConfigs')}</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenConfigDialog()}>
              {t('settings.sms.newConfig')}
            </Button>
          </Box>

          {/* Environment Tabs */}
          <Paper sx={{ mb: 2 }}>
            <Tabs
              value={configEnvTab}
              onChange={(_, newValue) => setConfigEnvTab(newValue)}
            >
              <Tab
                value="sandbox"
                icon={<SandboxIcon />}
                iconPosition="start"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t('settings.posSisp.sandbox')}
                    <Chip size="small" label={configs.filter(c => c.environment?.toLowerCase() === 'sandbox').length} color="info" />
                  </Box>
                }
              />
              <Tab
                value="production"
                icon={<ProductionIcon />}
                iconPosition="start"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t('settings.posSisp.production')}
                    <Chip size="small" label={configs.filter(c => c.environment?.toLowerCase() === 'production').length} color="success" />
                  </Box>
                }
              />
            </Tabs>
          </Paper>

          <Alert severity={configEnvTab === 'sandbox' ? 'info' : 'warning'} sx={{ mb: 2 }}>
            {configEnvTab === 'sandbox'
              ? t('settings.sms.sandboxSmsAlert')
              : t('settings.sms.productionSmsAlert')
            }
          </Alert>

          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.description')}</TableCell>
                    <TableCell>{t('settings.sms.accountSid')}</TableCell>
                    <TableCell>{t('settings.sms.sender')}</TableCell>
                    <TableCell align="center">{t('common.status')}</TableCell>
                    <TableCell align="center" width={120}>{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredConfigs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">{t('settings.sms.noConfig')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredConfigs.slice(configPage * 10, configPage * 10 + 10).map((config) => (
                      <TableRow key={config.id} hover>
                        <TableCell>
                          <Typography fontWeight={600}>{config.description}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontFamily="monospace" variant="body2">
                            {config.accountSid?.substring(0, 12)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label="Pagali" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={config.active === 'S' ? t('common.active') : t('common.inactive')}
                            color={config.active === 'S' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleOpenConfigDialog(config)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteConfig(config)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {filteredConfigs.length > 10 && (
              <TablePagination
                component="div"
                count={filteredConfigs.length}
                page={configPage}
                onPageChange={(_, p) => setConfigPage(p)}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
              />
            )}
          </Paper>
        </Box>
      )}

      {/* ==================== TEMPLATES TAB ==================== */}
      {activeTab === 'templates' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('settings.sms.smsTemplates')}</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenTemplateDialog()}>
              {t('settings.sms.newTemplate')}
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            {t('settings.sms.templatesInfo')}
          </Alert>

          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.code')}</TableCell>
                    <TableCell>{t('common.name')}</TableCell>
                    <TableCell>{t('common.type')}</TableCell>
                    <TableCell>{t('settings.sms.content')}</TableCell>
                    <TableCell align="center">{t('common.status')}</TableCell>
                    <TableCell align="center" width={120}>{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {templates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">{t('settings.sms.noTemplate')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    templates.slice(templatePage * 10, templatePage * 10 + 10).map((template) => (
                      <TableRow key={template.id} hover>
                        <TableCell>
                          <Typography fontFamily="monospace" fontWeight={600} variant="body2">
                            {template.code}
                          </Typography>
                        </TableCell>
                        <TableCell>{template.name}</TableCell>
                        <TableCell>{getTemplateTypeChip(template.type)}</TableCell>
                        <TableCell>
                          <Tooltip title={template.content}>
                            <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {template.content}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={template.active === 'S' ? t('common.active') : t('common.inactive')}
                            color={template.active === 'S' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleOpenTemplateDialog(template)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteTemplate(template)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {templates.length > 10 && (
              <TablePagination
                component="div"
                count={templates.length}
                page={templatePage}
                onPageChange={(_, p) => setTemplatePage(p)}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
              />
            )}
          </Paper>
        </Box>
      )}

      {/* ==================== HISTORY TAB ==================== */}
      {activeTab === 'history' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('settings.sms.sendHistory')}</Typography>
            <IconButton onClick={fetchLogs}>
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* Filters */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label={t('common.status')}
                  value={logFilters.status}
                  onChange={(e) => {
                    setLogFilters({ ...logFilters, status: e.target.value });
                    setLogPage(0);
                  }}
                >
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  <MenuItem value="pendente">Pendente</MenuItem>
                  <MenuItem value="enviado">Enviado</MenuItem>
                  <MenuItem value="entregue">Entregue</MenuItem>
                  <MenuItem value="falhou">Falhou</MenuItem>
                  <MenuItem value="rejeitado">Rejeitado</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('settings.sms.phone')}
                  value={logFilters.phoneNumber}
                  onChange={(e) => setLogFilters({ ...logFilters, phoneNumber: e.target.value })}
                  placeholder="+238..."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setLogFilters({ status: '', phoneNumber: '' });
                    setLogPage(0);
                  }}
                >
                  {t('settings.sms.clearFilters')}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('settings.sms.phone')}</TableCell>
                    <TableCell>{t('settings.sms.template')}</TableCell>
                    <TableCell>{t('settings.sms.content')}</TableCell>
                    <TableCell align="center">{t('common.status')}</TableCell>
                    <TableCell align="center">{t('settings.sms.segments')}</TableCell>
                    <TableCell>{t('common.date')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">{t('settings.sms.noSmsFound')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          <Typography fontFamily="monospace" variant="body2">{log.phoneNumber}</Typography>
                        </TableCell>
                        <TableCell>{log.templateName || '-'}</TableCell>
                        <TableCell>
                          <Tooltip title={log.content}>
                            <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {log.content}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">{getStatusChip(log.status)}</TableCell>
                        <TableCell align="center">{log.segments || 1}</TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {log.createdAt ? new Date(log.createdAt).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={logTotal}
              page={logPage}
              onPageChange={(_, p) => setLogPage(p)}
              rowsPerPage={10}
              rowsPerPageOptions={[10]}
              labelDisplayedRows={({ from, to, count }) => t('common.paginationDisplay', { from, to, count })}
            />
          </Paper>
        </Box>
      )}

      {/* ==================== INTERVALS TAB ==================== */}
      {activeTab === 'intervals' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('settings.sms.sendIntervals')}</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenIntervalDialog()}>
              {t('settings.sms.newInterval')}
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            {t('settings.sms.intervalsInfo')}
          </Alert>

          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.code')}</TableCell>
                    <TableCell>{t('common.description')}</TableCell>
                    <TableCell align="center">{t('settings.sms.intervalMin')}</TableCell>
                    <TableCell align="center">{t('common.status')}</TableCell>
                    <TableCell align="center" width={120}>{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {intervals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">{t('settings.sms.noInterval')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    intervals.slice(intervalPage * 10, intervalPage * 10 + 10).map((interval) => (
                      <TableRow key={interval.id} hover>
                        <TableCell>
                          <Typography fontFamily="monospace" fontWeight={600} variant="body2">
                            {interval.code}
                          </Typography>
                        </TableCell>
                        <TableCell>{interval.description}</TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={`${interval.minutes} min`} color="primary" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={interval.active === 'S' ? t('common.active') : t('common.inactive')}
                            color={interval.active === 'S' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleOpenIntervalDialog(interval)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteInterval(interval)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {intervals.length > 10 && (
              <TablePagination
                component="div"
                count={intervals.length}
                page={intervalPage}
                onPageChange={(_, p) => setIntervalPage(p)}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
              />
            )}
          </Paper>
        </Box>
      )}

      {/* ==================== DIALOGS ==================== */}

      {/* Config Dialog */}
      <Dialog open={configDialog} onClose={() => setConfigDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {configForm.environment === 'sandbox' ? <SandboxIcon color="info" /> : <ProductionIcon color="success" />}
          {selectedConfig ? t('settings.sms.editConfig') : t('settings.sms.newConfig')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label={t('common.description')}
                value={configForm.description}
                onChange={(e) => setConfigForm({ ...configForm, description: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={configForm.environment}
                onChange={(e) => setConfigForm({ ...configForm, environment: e.target.value as Environment })}
              >
                <MenuItem value="sandbox">{t('settings.posSisp.sandbox')}</MenuItem>
                <MenuItem value="production">{t('settings.posSisp.production')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Account SID"
                value={configForm.accountSid}
                onChange={(e) => setConfigForm({ ...configForm, accountSid: e.target.value })}
                inputProps={{ style: { fontFamily: 'monospace' } }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Auth Token"
                value={configForm.authToken}
                onChange={(e) => setConfigForm({ ...configForm, authToken: e.target.value })}
                inputProps={{ style: { fontFamily: 'monospace' } }}
                type={showToken ? 'text' : 'password'}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowToken(!showToken)} edge="end">
                        {showToken ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('settings.sms.twilioPhone')}
                value={configForm.phoneNumber}
                onChange={(e) => setConfigForm({ ...configForm, phoneNumber: e.target.value })}
                placeholder="+14246881297"
                helperText={t('settings.sms.twilioPhoneHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Messaging Service SID"
                value={configForm.messagingServiceSid}
                onChange={(e) => setConfigForm({ ...configForm, messagingServiceSid: e.target.value })}
                helperText={t('settings.sms.messagingServiceHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('settings.sms.voiceWebhook')}
                value={configForm.voiceWebhookUrl}
                onChange={(e) => setConfigForm({ ...configForm, voiceWebhookUrl: e.target.value })}
                placeholder="https://api.us.elevenlabs.io/twilio/inbound_call"
                helperText={t('settings.sms.voiceWebhookHelper')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VoiceIcon color="secondary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={configForm.active}
                    onChange={(e) => setConfigForm({ ...configForm, active: e.target.checked })}
                    color="success"
                  />
                }
                label={configForm.active ? t('common.active') : t('common.inactive')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSaveConfig}
            disabled={!configForm.description || !configForm.accountSid || !configForm.authToken}
          >
            {selectedConfig ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={templateDialog} onClose={() => setTemplateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedTemplate ? t('settings.sms.editTemplate') : t('settings.sms.newTemplate')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('common.code')}
                value={templateForm.code}
                onChange={(e) => setTemplateForm({ ...templateForm, code: e.target.value.toUpperCase() })}
                inputProps={{ style: { fontFamily: 'monospace' } }}
                disabled={!!selectedTemplate}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('common.type')}
                value={templateForm.type}
                onChange={(e) => setTemplateForm({ ...templateForm, type: e.target.value as any })}
              >
                <MenuItem value="transacional">{t('settings.sms.transactional')}</MenuItem>
                <MenuItem value="marketing">{t('settings.sms.marketing')}</MenuItem>
                <MenuItem value="notificacao">{t('settings.sms.notification')}</MenuItem>
                <MenuItem value="otp">{t('settings.sms.otp')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('common.name')}
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('settings.sms.content')}
                value={templateForm.content}
                onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })}
                multiline
                rows={4}
                required
                helperText={t('settings.sms.contentHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={templateForm.active}
                    onChange={(e) => setTemplateForm({ ...templateForm, active: e.target.checked })}
                    color="success"
                  />
                }
                label={templateForm.active ? t('common.active') : t('common.inactive')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={!templateForm.code || !templateForm.name || !templateForm.content}
          >
            {selectedTemplate ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Interval Dialog */}
      <Dialog open={intervalDialog} onClose={() => setIntervalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedInterval ? t('settings.sms.editInterval') : t('settings.sms.newInterval')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('common.code')}
                value={intervalForm.code}
                onChange={(e) => setIntervalForm({ ...intervalForm, code: e.target.value.toUpperCase() })}
                disabled={!!selectedInterval}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={t('settings.sms.intervalMinutes')}
                value={intervalForm.minutes}
                onChange={(e) => setIntervalForm({ ...intervalForm, minutes: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1 }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('common.description')}
                value={intervalForm.description}
                onChange={(e) => setIntervalForm({ ...intervalForm, description: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={intervalForm.active}
                    onChange={(e) => setIntervalForm({ ...intervalForm, active: e.target.checked })}
                    color="success"
                  />
                }
                label={intervalForm.active ? t('common.active') : t('common.inactive')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIntervalDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSaveInterval}
            disabled={!intervalForm.code || !intervalForm.description}
          >
            {selectedInterval ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send SMS Dialog */}
      <Dialog open={sendDialog} onClose={() => setSendDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon color="primary" />
          {t('settings.sms.sendSms')}
        </DialogTitle>
        <DialogContent dividers>
          {sending && <LinearProgress sx={{ mb: 2 }} />}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('settings.sms.phoneNumber')}
                value={sendForm.to}
                onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })}
                placeholder="+238XXXXXXX"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                select
                label={t('settings.sms.useTemplate')}
                value={sendForm.templateCode}
                onChange={(e) => setSendForm({ ...sendForm, templateCode: e.target.value })}
                helperText={t('settings.sms.selectTemplateHelper')}
              >
                <MenuItem value="">{t('settings.sms.customMessage')}</MenuItem>
                {templates.filter(t => t.active === 'S').map((t) => (
                  <MenuItem key={t.id} value={t.code}>{t.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {!sendForm.templateCode && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={t('settings.sms.message')}
                  value={sendForm.message}
                  onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
                  multiline
                  rows={4}
                  required
                />
              </Grid>
            )}
            {sendForm.templateCode && (() => {
              const tpl = templates.find(t => t.code === sendForm.templateCode);
              const content = tpl?.content || '';
              // Extrair variáveis {{xxx}} do template
              const varMatches = Array.from(content.matchAll(/\{\{\s*(\w+)\s*\}\}/g));
              const uniqueVars = Array.from(new Set(varMatches.map(m => m[1])));
              // Render preview substituindo variáveis preenchidas
              const preview = uniqueVars.reduce(
                (acc, v) => acc.replace(new RegExp(`\\{\\{\\s*${v}\\s*\\}\\}`, 'g'), sendForm.variables[v] || `{{${v}}}`),
                content
              );
              return (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>{t('settings.sms.templateSelected')}</strong> {preview}
                      </Typography>
                    </Alert>
                  </Grid>
                  {uniqueVars.length > 0 && uniqueVars.map((v) => (
                    <Grid key={v} size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label={v}
                        value={sendForm.variables[v] || ''}
                        onChange={(e) => setSendForm({
                          ...sendForm,
                          variables: { ...sendForm.variables, [v]: e.target.value },
                        })}
                        placeholder={`Valor para {{${v}}}`}
                      />
                    </Grid>
                  ))}
                </>
              );
            })()}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialog(false)} disabled={sending}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSendSms}
            disabled={!sendForm.to || (!sendForm.templateCode && !sendForm.message) || sending}
            startIcon={<SendIcon />}
          >
            {sending ? t('settings.sms.sending') : t('settings.sms.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SmsPage;
